import React, { useState, useEffect, useRef } from 'react';
import styles from './Translator.module.scss';
import { getTranslatorBootstrap, getTranslatedText } from 'integration';
import defaultLanguages from 'data/mock/translatorLanguages.json';
import { Copy } from '@zurich-data-dsm-icons/action.copy';
import { Check } from '@zurich-data-dsm-icons/action.check';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as SoundOnIcon } from 'app/assets/Icons/SoundOn.svg';
import { ReactComponent as MicrophoneOnIcon } from 'app/assets/Icons/MicrophoneOn.svg';
import { LanguageSelector } from 'app/components/LanguageSelector';
import { ReactComponent as SwitchIcon } from 'app/assets/Icons/ArrowLeftArrowRight.svg';
import iso6391 from 'iso-639-1';
import { Tooltip } from 'react-tooltip';
import { SpeechFeaturesTooltipContent } from 'app/utils/consts';
import { LoadingSpinner } from 'app/components/LoadingSpinner';
import { ReactComponent as CloseIcon } from 'app/assets/Icons/Close.svg';

interface Language {
  name: string;
  labelName: string;
  dialect: string;
}

interface TranslatorProps {
  medicalMode: boolean;
}

const DEFAULT_DETECTED_LANGUAGE_LABEL = 'Detected language';

export const Translator: React.FC<TranslatorProps> = ({ medicalMode }) => {
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] =
    useState<string>('English (GB)');
  const [selectedDialect, setSelectedDialect] = useState<string | undefined>(
    'GB'
  );
  const [detectedLanguage, setDetectedLanguage] = useState<string>(
    DEFAULT_DETECTED_LANGUAGE_LABEL
  );
  const [detectedDialect, setDetectedDialect] = useState<string | undefined>();
  const [isSwitchedLanguage, setIsSwitchedLanguage] = useState<boolean>(false);

  const [copyIcon, setCopyIcon] = React.useState<React.JSX.Element>(<Copy />);
  const inputTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const outputTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState<boolean>(false);

  useEffect(() => {
    setIsLoadingLanguages(true);
    getTranslatorBootstrap('cross-functional', 'translator')
      .then((response) => {
        const availableLanguages = response.availableLanguages;
        const sortedLanguages = availableLanguages.sort(
          (a: Language, b: Language) => a.labelName.localeCompare(b.labelName)
        );
        setAvailableLanguages(sortedLanguages);
      })
      .finally(() => setIsLoadingLanguages(false))
      .catch((error) => {
        console.error('Error getting translator bootstrap data:', error);
      });
  }, []);

  useEffect(() => {
    const delayAPICall = setTimeout(
      () => {
        const trimmed = inputText.trim();
        if (trimmed === '') {
          setOutputText('');
          setIsSwitchedLanguage(false);
          return;
        }
        const foundLanguageName = availableLanguages
          ? availableLanguages?.find(
              ({ labelName }) => labelName === selectedLanguage
            )
          : defaultLanguages?.find(
              ({ labelName }) => labelName === selectedLanguage
            );

        translateText(
          inputText,
          foundLanguageName?.name ?? selectedLanguage?.split(' (')[0] ?? '',
          foundLanguageName?.dialect,
          isSwitchedLanguage
        );
        setIsSwitchedLanguage(false);
      },
      medicalMode && inputText.trim() ? 0 : 1000
    );

    return () => clearTimeout(delayAPICall);
  }, [inputText, medicalMode]);

  useEffect(() => {
    if (inputTextAreaRef.current) {
      inputTextAreaRef.current.style.height = 'auto';
      inputTextAreaRef.current.style.height = `${inputTextAreaRef.current.scrollHeight}px`;
      if (outputTextAreaRef.current) {
        outputTextAreaRef.current.style.height = 'auto';
        outputTextAreaRef.current.style.height = `${inputTextAreaRef.current.scrollHeight}px`;
      }
    }
  }, [inputText, outputText]);

  useEffect(() => {}, [isLoading, isLoadingLanguages, isSwitchedLanguage]);

  const onTranslationResponse = (
    response: any,
    setTranslation: (translation: string) => void,
    switchedLanguages?: boolean
  ): void => {
    let detectedLanguage: string;
    if (response.detectedLanguage) {
      detectedLanguage = iso6391.getName(response?.detectedLanguage);
    } else {
      detectedLanguage = DEFAULT_DETECTED_LANGUAGE_LABEL;
    }
    setTranslation(response.result ?? '');

    const foundLanguageLabelName = availableLanguages
      ? availableLanguages?.find(
          ({ name, dialect }) =>
            name === detectedLanguage && dialect === response?.detectedDialect
        )
      : defaultLanguages?.find(
          ({ name, dialect }) =>
            name === detectedLanguage && dialect === response?.detectedDialect
        );

    if (!switchedLanguages) {
      setDetectedLanguage(
        foundLanguageLabelName?.labelName ?? detectedLanguage
      );
      setDetectedDialect(foundLanguageLabelName?.dialect);
    }
  };

  const translateText = async (
    text: string,
    language: string,
    dialect?: string,
    switchedLanguages?: boolean
  ): Promise<void> => {
    setIsLoading(true);
    const languageCode = iso6391.getCode(language);
    const requestBody = {
      text,
      outputLanguage: languageCode,
      outputDialect: dialect,
      medicalMode: medicalMode
    };
    getTranslatedText('cross-functional', 'translator', requestBody)
      .then((response) => {
        onTranslationResponse(response, setOutputText, switchedLanguages);
      })
      .finally(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    setInputText(e.target.value);
  };

  const handleOnLanguageSelectorChange = (e: {
    target: { value: any };
  }): void => {
    const language = String(e.target.value?.name);
    const languageLabel = String(e.target.value?.labelName);
    const dialect = String(e.target.value?.dialect);

    setSelectedLanguage(languageLabel);
    setSelectedDialect(dialect);

    if (inputText) {
      const text = inputText;
      translateText(text, language, dialect);
    }
  };

  const copyTranslation = async (): Promise<void> => {
    const fieldElement = document.getElementById('output-area');
    const textAreaElement = fieldElement?.getElementsByTagName('textarea')[0];

    await navigator.clipboard.writeText(
      (textAreaElement as HTMLTextAreaElement).value ?? ''
    );
    setCopyIcon(<Check />);
    globalThis.setTimeout(() => setCopyIcon(<Copy />), 3000);
  };

  const switchLanguages = (): void => {
    if (detectedLanguage && outputText) {
      const newOutputLanguage = detectedLanguage;
      const newOutputDialect = detectedDialect;
      setDetectedLanguage(selectedLanguage);
      setDetectedDialect(selectedDialect);
      setSelectedLanguage(newOutputLanguage);
      setSelectedDialect(newOutputDialect);
      setInputText(outputText);
      setOutputText('');
      setIsSwitchedLanguage(true);
    }
  };

  const deleteInputText = (): void => {
    setInputText('');
    setOutputText('');
  };

  return (
    <div className={styles.translator}>
      <div className={styles['translator-container']}>
        <div className={styles.header}>
          <div className={styles['detected-language']}>
            {detectedLanguage && <p>{detectedLanguage}</p>}
          </div>
          <Button
            variant="tertiary"
            aria-label="Speak"
            disabled={!detectedLanguage}
            onClick={switchLanguages}
          >
            <Button.Icon>
              <SwitchIcon />
            </Button.Icon>
          </Button>
          {isLoading ? (
            <LoadingSpinner
              containerStyle={{
                position: 'relative',
                left: '2rem',
                width: 'auto',
                height: 'auto',
                backdropFilter: 'unset'
              }}
              imageStyle={{ width: '6rem', height: '2rem', left: '2rem' }}
            />
          ) : null}

          <LanguageSelector
            languages={availableLanguages || defaultLanguages}
            selectedLanguage={selectedLanguage}
            handleOnLanguageSelectorChange={handleOnLanguageSelectorChange}
            isLoading={isLoadingLanguages}
          />
        </div>
        <div className={styles['text-area-container']}>
          <div className={styles['input-area']}>
            <div className={styles['textarea-wrapper']}>
              <textarea
                placeholder="Type to translate"
                ref={inputTextAreaRef}
                value={inputText}
                onChange={handleInputChange}
                className={styles['text-area']}
              />
              <Button
                variant="tertiary"
                aria-label="Delete Text"
                onClick={deleteInputText}
                className={styles['delete-button']}
              >
                Delete Text
                <Button.Icon>
                  <span className={styles['custom-icon']}>
                    <CloseIcon className={styles['delete-icon']} />
                  </span>
                </Button.Icon>
              </Button>
            </div>

            <div className={styles['toolbar-input']}>
              <Button variant="tertiary" aria-label="Speak" disabled>
                <Button.Icon>
                  <MicrophoneOnIcon
                    data-tooltip-id="speech-features-tooltip"
                    data-tooltip-content={SpeechFeaturesTooltipContent}
                  />
                </Button.Icon>
              </Button>
              <Button
                variant="tertiary"
                aria-label="Listen"
                disabled
                data-tooltip-id="speech-features-tooltip"
                data-tooltip-content={SpeechFeaturesTooltipContent}
              >
                <Button.Icon>
                  <SoundOnIcon />
                </Button.Icon>
              </Button>
            </div>
          </div>
          <div id="output-area" className={styles['output-area']}>
            <textarea
              value={outputText}
              ref={outputTextAreaRef}
              readOnly
              className={styles['text-area']}
            />
            <div className={styles['toolbar-output']}>
              <div className={styles['toolbar-left']}>
                <Button
                  variant="tertiary"
                  aria-label="Listen"
                  disabled
                  data-tooltip-id="speech-features-tooltip"
                  data-tooltip-content={SpeechFeaturesTooltipContent}
                >
                  <Button.Icon>
                    <SoundOnIcon />
                  </Button.Icon>
                </Button>
              </div>
              <div className={styles['toolbar-right']}>
                <Button
                  variant="tertiary"
                  aria-label="Copy to clipboard"
                  onClick={copyTranslation}
                >
                  <Button.Icon>{copyIcon}</Button.Icon>
                </Button>
              </div>
            </div>
            <Tooltip
              id="speech-features-tooltip"
              className={styles['speech-features-tooltip']}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
